import React, { useState, useEffect, FC, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TextFieldProps,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormHelperText,
  Typography,
  Box,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
  Switch,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { createPromotion, getClientVariables, ClientVariable } from '../../services/promotionService';
import ImageUpload from '../common/ImageUpload';
import { useSnackbar } from 'notistack';

interface FormData {
  img: string;
  title: string;
  code: string;
  client_points: number;
  consumer_limit: number;
  client_limit: number;
  start_date: Date | null;
  end_date: Date | null;
  terms: string;
  channel: string;
  gec: string;
  recipients: string;
  status: 'activo' | 'inactivo';
}

interface AddPromotionDialogProps {
  open: boolean;
  onClose: (created?: boolean) => void;
}

const AddPromotionDialog: FC<AddPromotionDialogProps> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State for form data and validation
  const [formData, setFormData] = useState<FormData>({
    img: '',
    title: '',
    code: '',
    client_points: 0,
    consumer_limit: 1,
    client_limit: 1,
    start_date: null,
    end_date: null,
    terms: '',
    channel: '',
    gec: '',
    recipients: '',
    status: 'activo' as const
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientVariables, setClientVariables] = useState<{ 
    channels: ClientVariable[]; 
    gec: ClientVariable[] 
  }>({ 
    channels: [],
    gec: []
  });
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [recipientType, setRecipientType] = useState<'segment' | 'client'>('segment');

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido';
    }
    
    if (!formData.start_date || !isValid(formData.start_date)) {
      newErrors.start_date = 'La fecha de inicio es requerida';
    }
    
    if (!formData.end_date || !isValid(formData.end_date)) {
      newErrors.end_date = 'La fecha de fin es requerida';
    } else if (formData.start_date && formData.end_date < formData.start_date) {
      newErrors.end_date = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }
    
    if (!formData.terms.trim()) {
      newErrors.terms = 'Los términos y condiciones son requeridos';
    }
    
    if (recipientType === 'segment') {
      if (!formData.channel.trim()) {
        newErrors.channel = 'El canal es requerido';
      }
      if (!formData.gec.trim()) {
        newErrors.gec = 'El GEC es requerido';
      }
    } else if (!formData.recipients.trim()) {
      newErrors.recipients = 'Debe ingresar al menos un ID de cliente';
    }

    // Validar que los límites sean al menos 1
    if (formData.consumer_limit < 1) {
      newErrors.consumer_limit = 'El límite por consumidor debe ser al menos 1';
    }
    
    if (formData.client_limit < 1) {
      newErrors.client_limit = 'El límite por cliente debe ser al menos 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, recipientType]);

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }> | { target: { name?: string; value: unknown } }) => {
    const name = event.target.name;
    const value = 'type' in event.target && event.target.type === 'checkbox' 
      ? (event.target as HTMLInputElement).checked 
      : event.target.value;
    
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when user starts typing
      if (errors[name as keyof typeof errors]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked ? 'activo' : 'inactivo'
    }));
  };

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      const numericValue = value === '' ? 0 : parseInt(value, 10);
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      
      // Clear error when value changes
      if (errors[name as keyof typeof errors] && numericValue >= 1) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleDateChange = (name: 'start_date' | 'end_date') => (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
    
    // Clear error when date is selected
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (event: { target: { name?: string; value: unknown } }) => {
    const { name, value } = event.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when user selects an option
      if (errors[name as keyof typeof errors]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  // Load client variables on component mount
  useEffect(() => {
    const loadClientVariables = async () => {
      try {
        setLoadingVariables(true);
        const data = await getClientVariables();
        setClientVariables(data);
      } catch (error) {
        console.error('Error loading client variables:', error);
        enqueueSnackbar('Error al cargar las variables del cliente', { variant: 'error' });
      } finally {
        setLoadingVariables(false);
      }
    };

    if (open) {
      loadClientVariables();
    }
  }, [open, enqueueSnackbar]);

  const handleRecipientTypeChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRecipientType(value as 'segment' | 'client');
    
    // Clear related fields when changing recipient type
    setFormData(prev => ({
      ...prev,
      channel: '',
      gec: '',
      recipients: ''
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validate()) return;
    
    if (!imageFile) {
      setErrors(prev => ({ ...prev, image: 'Debes subir una imagen para la promoción' }));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Upload image and get URL
      let imageUrl = formData.img; // Use existing URL if available
      
      if (imageFile) {
        // TODO: Implement image upload to Firebase Storage
        // imageUrl = await uploadImageToFirebase(imageFile);
      }
      
      // Prepare promotion data
      const promotionData = {
        ...formData,
        img: imageUrl,
        start_date: formData.start_date ? format(formData.start_date, 'yyyy-MM-dd') : '',
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : '',
        status: formData.status === 'activo' ? 1 : 0
      };
      
      await createPromotion(promotionData, imageFile);
      enqueueSnackbar('Promoción creada exitosamente', { variant: 'success' });
      onClose(true);
    } catch (error) {
      console.error('Error creating promotion:', error);
      enqueueSnackbar('Error al crear la promoción', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose(false);
    }
  }, [isSubmitting, onClose]);

  const handleImageChange = (file: File | null) => {
    setImageFile(file);
    if (errors.image) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });
    }
  };

  const handleCloseDialog = useCallback(() => {
    onClose();
    // Reset form when closing
    setFormData({
      img: '',
      title: '',
      code: '',
      client_points: 0,
      consumer_limit: 1,
      client_limit: 1,
      start_date: null,
      end_date: null,
      terms: '',
      channel: '',
      gec: '',
      recipients: '',
      status: 'activo' as const
    });
    setImageFile(null);
    setRecipientType('segment');
    setErrors({});
  }, [onClose]);

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Nueva Promoción</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errors.form && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {errors.form}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 2 }}>
            <Box sx={{ flex: 1 }}>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  name="title"
                  label="Título *"
                  fullWidth
                  margin="normal"
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title || ' '}
                  required
                  sx={{ flex: 2 }}
                />
                
                <TextField
                  name="code"
                  label="Código"
                  fullWidth
                  margin="normal"
                  value={formData.code}
                  onChange={handleChange}
                  helperText="Opcional"
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                <TextField
                  name="client_points"
                  label="Puntos por cliente"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.client_points}
                  onChange={handleChange}
                  error={!!errors.client_points}
                  helperText={errors.client_points}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  name="consumer_limit"
                  label="Límite por consumidor"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.consumer_limit}
                  onChange={handleChange}
                  error={!!errors.consumer_limit}
                  helperText={errors.consumer_limit || ' '}
                  InputProps={{
                    inputProps: { 
                      min: 1,
                      step: 1
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  name="client_limit"
                  label="Límite por cliente"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.client_limit}
                  onChange={handleChange}
                  error={!!errors.client_limit}
                  helperText={errors.client_limit || ' '}
                  InputProps={{
                    inputProps: { 
                      min: 1,
                      step: 1
                    }
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                  <DesktopDatePicker
                    label="Fecha de inicio *"
                    value={formData.start_date}
                    onChange={handleDateChange('start_date')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal' as const,
                        error: !!errors.start_date,
                        helperText: errors.start_date,
                      } as TextFieldProps
                    }}
                  />
                  
                  <DesktopDatePicker
                    label="Fecha de fin *"
                    value={formData.end_date}
                    onChange={handleDateChange('end_date')}
                    minDate={formData.start_date || undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal' as const,
                        error: !!errors.end_date,
                        helperText: errors.end_date,
                      } as TextFieldProps
                    }}
                  />
                </Box>
              </LocalizationProvider>
              
              <TextField
                name="terms"
                label="Términos y condiciones *"
                fullWidth
                margin="normal"
                value={formData.terms}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.terms}
                helperText={errors.terms}
              />
              
              <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                <FormLabel component="legend">Destinatarios</FormLabel>
                <RadioGroup
                  row
                  value={recipientType}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setRecipientType(e.target.value as 'segment' | 'client')
                  }
                >
                  <FormControlLabel value="segment" control={<Radio />} label="Por segmento" />
                  <FormControlLabel value="client" control={<Radio />} label="Por cliente" />
                </RadioGroup>
                
                {recipientType === 'segment' ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                    <FormControl fullWidth margin="normal" error={!!errors.channel}>
                      <InputLabel id="channel-label">Canal *</InputLabel>
                      <Select
                        labelId="channel-label"
                        name="channel"
                        value={formData.channel}
                        onChange={handleChange}
                        label="Canal *"
                        disabled={loadingVariables}
                      >
                        <MenuItem value="">
                          <em>Seleccionar canal</em>
                        </MenuItem>
                        {clientVariables.channels.map((channel) => (
                          <MenuItem key={channel.id} value={channel.id}>
                            {channel.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.channel && <FormHelperText>{errors.channel}</FormHelperText>}
                    </FormControl>
                    
                    <FormControl fullWidth margin="normal" error={!!errors.gec}>
                      <InputLabel id="gec-label">GEC *</InputLabel>
                      <Select
                        labelId="gec-label"
                        name="gec"
                        value={formData.gec}
                        onChange={handleChange}
                        label="GEC *"
                        disabled={loadingVariables}
                      >
                        <MenuItem value="">
                          <em>Seleccionar GEC</em>
                        </MenuItem>
                        {clientVariables.gec.map((gec) => (
                          <MenuItem key={gec.id} value={gec.id}>
                            {gec.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.gec && <FormHelperText>{errors.gec}</FormHelperText>}
                    </FormControl>
                  </Box>
                ) : (
                  <TextField
                    name="recipients"
                    label="IDs de clientes (separados por comas)"
                    fullWidth
                    margin="normal"
                    value={formData.recipients}
                    onChange={handleChange}
                    error={!!errors.recipients}
                    helperText={errors.recipients || 'Ingrese los IDs de los clientes separados por comas'}
                    multiline
                    rows={2}
                  />
                )}
              </FormControl>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              mt: { xs: 2, md: 0 },
              width: { xs: '100%', md: 250 },
              flexShrink: 0
            }}>
              <Typography variant="subtitle2" gutterBottom>
                Imagen de la promoción
              </Typography>
              <ImageUpload
                onChange={handleImageChange}
                label="Subir imagen"
              />
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                  Formatos permitidos: webp, png, jpg
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                  Peso máximo: 1MB
                </Typography>
              </Box>
              {errors.image && (
                <FormHelperText error sx={{ mt: 1 }}>{errors.image}</FormHelperText>
              )}
              
              <Box mt={3} width="100%" textAlign="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === 'activo'}
                      onChange={handleStatusChange}
                      color="primary"
                    />
                  }
                  label={formData.status === 'activo' ? 'Activo' : 'Inactivo'}
                  labelPlacement="start"
                  sx={{ 
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 1,
                      fontWeight: 'medium'
                    }
                  }}
                />
                
                <Box mt={4} display="flex" flexDirection="column" gap={2}>
                  <Button 
                    onClick={handleClose} 
                    disabled={isSubmitting}
                    variant="outlined"
                    fullWidth
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    disabled={isSubmitting}
                    fullWidth
                  >
                    {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Form fields */}
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default React.memo(AddPromotionDialog);
